import { create } from 'zustand';
import { AUTO_LOCK, PIN } from '../lib/rcu/catalog/security';
import type { PinGateOutcome } from '../lib/technician/catalog';
import { MS_PER_MINUTE, PIN_GATE_OUTCOME } from '../lib/technician/catalog';
import { createPin, isValidPinFormat, verifyPin } from '../lib/technician/pin';
import type { TechnicianState } from '../lib/technician/state';
import {
  isAutoLockOption,
  loadStoredTechnicianState,
  lockoutRemainingMs,
  registerFailedAttempt,
  registerUnlock,
  saveStoredTechnicianState,
} from '../lib/technician/state';

/** What the PIN gate tells the UI after an attempt. */
export interface PinAttempt {
  readonly outcome: PinGateOutcome;
  readonly unlocked: boolean;
  /** Tries left before the cool-down, after this attempt. */
  readonly attemptsRemaining: number;
  /** Milliseconds until guessing is allowed again, or zero. */
  readonly lockoutMs: number;
}

interface TechnicianStore {
  readonly state: TechnicianState;
  readonly isUnlocked: boolean;
  /** Wall clock of the last interaction; drives the idle auto-lock. */
  readonly lastActivityMs: number;

  unlock: (pin: string) => PinAttempt;
  lock: () => void;
  noteActivity: (nowMs: number) => void;
  /** Locks the console when the idle window has elapsed. Returns true if it did. */
  lockIfIdle: (nowMs: number) => boolean;

  changePin: (currentPin: string, nextPin: string, confirmation: string) => PinAttempt;
  /** Recovery for a forgotten PIN: confirm the RCU device id, then reset to 0000. */
  resetPin: (deviceIdConfirmation: string, expectedDeviceId: string) => PinGateOutcome;
  setAutoLockMinutes: (minutes: number) => void;

  lockoutRemainingMs: (nowMs: number) => number;
  attemptsRemaining: () => number;
}

const attempt = (
  outcome: PinGateOutcome,
  state: TechnicianState,
  nowMs: number,
): PinAttempt => ({
  outcome,
  unlocked: outcome === PIN_GATE_OUTCOME.OK,
  attemptsRemaining: PIN.MAX_ATTEMPTS - state.failedAttempts,
  lockoutMs: lockoutRemainingMs(state, nowMs),
});

export const useTechnicianStore = create<TechnicianStore>((set, get) => {
  /** Replaces state and persists it. PIN material is never kept only in memory. */
  const commit = (state: TechnicianState): void => {
    set({ state });
    saveStoredTechnicianState(state);
  };

  const rejectPin = (outcome: PinGateOutcome, nowMs: number): PinAttempt => {
    const state = registerFailedAttempt(get().state, nowMs);
    commit(state);
    return attempt(outcome, state, nowMs);
  };

  return {
    state: loadStoredTechnicianState(),
    isUnlocked: false,
    lastActivityMs: 0,

    unlock: (pin) => {
      const nowMs = Date.now();
      const { state } = get();

      if (lockoutRemainingMs(state, nowMs) > 0) {
        return attempt(PIN_GATE_OUTCOME.LOCKED_OUT, state, nowMs);
      }
      if (!isValidPinFormat(pin)) return rejectPin(PIN_GATE_OUTCOME.INVALID_FORMAT, nowMs);
      if (!verifyPin(pin, state.pin)) return rejectPin(PIN_GATE_OUTCOME.WRONG_PIN, nowMs);

      const unlocked = registerUnlock(state);
      commit(unlocked);
      set({ isUnlocked: true, lastActivityMs: nowMs });
      return attempt(PIN_GATE_OUTCOME.OK, unlocked, nowMs);
    },

    lock: () => set({ isUnlocked: false }),

    noteActivity: (nowMs) => set({ lastActivityMs: nowMs }),

    lockIfIdle: (nowMs) => {
      const { isUnlocked, lastActivityMs, state } = get();
      if (!isUnlocked) return false;
      if (state.autoLockMinutes === AUTO_LOCK.NEVER_MINUTES) return false;

      const idleMs = nowMs - lastActivityMs;
      if (idleMs < state.autoLockMinutes * MS_PER_MINUTE) return false;

      set({ isUnlocked: false });
      return true;
    },

    changePin: (currentPin, nextPin, confirmation) => {
      const nowMs = Date.now();
      const { state } = get();

      if (!isValidPinFormat(nextPin) || !isValidPinFormat(confirmation)) {
        return attempt(PIN_GATE_OUTCOME.INVALID_FORMAT, state, nowMs);
      }
      if (nextPin !== confirmation) return attempt(PIN_GATE_OUTCOME.PIN_MISMATCH, state, nowMs);
      if (!verifyPin(currentPin, state.pin)) {
        return attempt(PIN_GATE_OUTCOME.WRONG_CURRENT, state, nowMs);
      }

      const changed = registerUnlock({ ...state, pin: createPin(nextPin) });
      commit(changed);
      return attempt(PIN_GATE_OUTCOME.PIN_CHANGED, changed, nowMs);
    },

    resetPin: (deviceIdConfirmation, expectedDeviceId) => {
      const matches =
        deviceIdConfirmation.trim() !== '' &&
        deviceIdConfirmation.trim() === expectedDeviceId.trim();
      if (!matches) return PIN_GATE_OUTCOME.DEVICE_ID_MISMATCH;

      commit(
        registerUnlock({
          ...get().state,
          pin: createPin(PIN.DEFAULT),
        }),
      );
      return PIN_GATE_OUTCOME.PIN_RESET;
    },

    setAutoLockMinutes: (minutes) => {
      if (!isAutoLockOption(minutes)) return;
      commit({ ...get().state, autoLockMinutes: minutes });
    },

    lockoutRemainingMs: (nowMs) => lockoutRemainingMs(get().state, nowMs),

    attemptsRemaining: () => PIN.MAX_ATTEMPTS - get().state.failedAttempts,
  };
});

export default useTechnicianStore;
